import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import { GetFunctionsByEventId, GetGuestsByEventId, GetGuestsByEventIdForMember } from '../../api/api';
import { cardNameOf, cardSubtitleOf } from '../cards/useCards';
import { loadTemplate } from '../labelSetup/useLabelSetup';
import { needsCard } from '../../utils/helpers';
import { buildLabelsPdf, labelPositions, perSheet } from '../../utils/labelSheet';

const rowFunctionIds = (row) => {
  if (row.FunctionIds) return String(row.FunctionIds).split(',').map(v => v.trim()).filter(Boolean);
  return row.FunctionId ? [String(row.FunctionId)] : [];
};

// Printing the stickers: which invitation cards, how many of each, where on the
// sheet to start, then a PDF in exact millimetres (a browser's own print shifts
// by a few mm between printers, which ruins a sheet of stickers).
export function useLabels(event) {
  const { user, isAdmin, can } = useAuth();
  const toast = useToast();
  const me = user.UserId;

  const canView = isAdmin || can('CanMembers');
  const [rows, setRows] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tpl, setTpl] = useState(() => loadTemplate(event?.EventId));
  useEffect(() => { setTpl(loadTemplate(event?.EventId)); }, [event]);

  const [fnFilter, setFnFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');      // '' | Family | Business
  const [q, setQ] = useState('');
  const [withExtras, setWithExtras] = useState(true);    // a head with +2 gets 3 stickers
  const [startAt, setStartAt] = useState(1);             // first free sticker on a part-used sheet
  const [picked, setPicked] = useState(null);            // null = every card in view
  const [busy, setBusy] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');

  const refresh = useCallback(() => {
    if (!canView) return;
    setLoading(true);
    const loader = isAdmin
      ? GetGuestsByEventId({ EventId: event.EventId })
      : GetGuestsByEventIdForMember({ EventId: event.EventId, UserId: me });
    loader.then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => toast('Failed to load guests'))
      .finally(() => setLoading(false));
  }, [event, isAdmin, me, canView, toast]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    GetFunctionsByEventId({ EventId: event.EventId })
      .then(d => setFunctions(Array.isArray(d) ? d : [])).catch(() => setFunctions([]));
  }, [event]);

  // One entry per invitation card: a household under its head, a firm under its
  // owner, anyone else on their own (the same grouping the Cards page prints).
  const allCards = useMemo(() => {
    const byKey = new Map();
    // The hosts' own family (Guest Type 'Own family') gets no card, so no sticker.
    rows.filter(needsCard).forEach(r => {
      const famId = Number(r.FamilyId || 0);
      const key = famId ? `fam:${famId}` : (r.MobileNumber ? `mob:${r.MobileNumber}` : `g:${r.GuestId}`);
      if (!byKey.has(key)) byKey.set(key, { key, head: r, members: [], fnIds: new Set(), functionNames: new Set() });
      const card = byKey.get(key);
      if (famId && String(r.GuestId) === String(famId)) card.head = r;
      if (!card.members.some(m => String(m.GuestId) === String(r.GuestId))) card.members.push(r);
      rowFunctionIds(r).forEach(id => card.fnIds.add(String(id)));
      String(r.FunctionNames || r.FunctionName || '').split(',').map(v => v.trim()).filter(Boolean)
        .forEach(nm => card.functionNames.add(nm));
    });
    return [...byKey.values()].map(c => ({
      key: c.key,
      name: cardNameOf(c.head),
      subtitle: cardSubtitleOf(c.head),
      type: String(c.head.GuestType || 'Family'),
      city: c.head.City || '',
      mobile: c.head.MobileNumber || '',
      persons: c.members.length,
      functions: [...c.functionNames],
      fnIds: c.fnIds,
      extraCards: c.members.reduce((s, m) => Math.max(s, Number(m.ExtraCards || 0)), 0),
    })).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [rows]);

  const cards = useMemo(() => {
    let list = allCards;
    if (fnFilter) list = list.filter(c => c.fnIds.has(String(fnFilter)));
    if (typeFilter) list = list.filter(c => c.type === typeFilter);
    const needle = q.trim().toLowerCase();
    if (needle) list = list.filter(c => `${c.name} ${c.subtitle} ${c.city} ${c.mobile}`.toLowerCase().includes(needle));
    return list;
  }, [allCards, fnFilter, typeFilter, q]);

  // `picked` null means "everything in view"; otherwise the ticked keys.
  const chosen = useMemo(
    () => (picked ? cards.filter(c => picked.includes(c.key)) : cards),
    [cards, picked]
  );
  const toggle = (key) => setPicked(list => {
    const base = list || cards.map(c => c.key);
    return base.includes(key) ? base.filter(k => k !== key) : [...base, key];
  });
  const allOn = !picked || picked.length === cards.length;
  const toggleAll = () => setPicked(allOn ? [] : null);

  const stickers = useMemo(
    () => chosen.reduce((s, c) => s + 1 + (withExtras ? Math.max(Number(c.extraCards || 0), 0) : 0), 0),
    [chosen, withExtras]
  );
  const slots = perSheet(tpl.sheet);
  const sheets = slots ? Math.ceil(Math.max(stickers + Math.max(Number(startAt) - 1, 0), 0) / slots) : 0;
  const positions = useMemo(() => labelPositions(tpl.sheet, tpl.offsetX, tpl.offsetY), [tpl]);

  const make = useCallback(() => buildLabelsPdf({
    template: tpl, cards: chosen, eventName: event?.EventName,
    startAt: Math.max(Number(startAt) || 1, 1), withExtras,
  }), [tpl, chosen, event, startAt, withExtras]);

  // The preview is the real PDF — what you see is what the printer gets.
  useEffect(() => {
    let dead = false;
    if (!chosen.length) { setPreviewUrl(''); return undefined; }
    setBusy('preview');
    make().then(blob => {
      if (dead) return;
      setPreviewUrl(old => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(blob); });
    }).catch(() => { if (!dead) toast('Could not build the sheet'); })
      .finally(() => { if (!dead) setBusy(''); });
    return () => { dead = true; };
  }, [make, chosen.length, toast]);

  const run = async (what, fn) => { setBusy(what); try { await fn(); } catch { toast('Could not make the labels'); } finally { setBusy(''); } };

  const savePdf = () => run('save', async () => {
    const blob = await make();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${event?.EventName || 'Labels'} — labels.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    toast('Labels saved');
  });

  const print = () => run('print', async () => {
    const blob = await make();
    const url = URL.createObjectURL(blob);
    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
    frame.src = url;
    frame.onload = () => { try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch { window.open(url, '_blank'); } };
    document.body.appendChild(frame);
    setTimeout(() => { frame.remove(); URL.revokeObjectURL(url); }, 60000);
  });

  return {
    canView, loading, event, functions, tpl,
    cards, chosen, picked, toggle, allOn, toggleAll,
    fnFilter, setFnFilter, typeFilter, setTypeFilter, q, setQ,
    withExtras, setWithExtras, startAt, setStartAt,
    stickers, slots, sheets, positions, previewUrl, busy,
    savePdf, print, refresh,
  };
}
